import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Response } from 'cross-fetch'
import debounce from 'lodash.debounce'

import { useAppDispatch } from 'state/store'

import {
  AutocompleteSuggestion,
  FilteractionDataSource,
  FlatInteractionModelData
} from 'types/shared'
import api from 'utils/api'
import { FilteractionParams } from 'utils/apiTypes'
import { FetchSuggestionsHook } from './useSuggestionsFromSources'
import useSourceActivityValues from './useSourceActivityValues/useSourceActivityValues'
import { YesObject } from '@yes.technology/react-toolkit'
import { concatenate, getProjectFields } from 'utils/concatenate'

type FetchSuggestionsArgsProps = Parameters<FetchSuggestionsHook>[0] & {
  activityValues?: FlatInteractionModelData['activity_values']
}

const useFetchSuggestions: FetchSuggestionsHook = ({
  parentPath,
  filteractionParams: legacyFilteractionParams,
  filteraction: legacyFilteraction,
  endpoint: legacyEndpoint,
  showLoading = false,
  projectFields,
  dataSources,
  onSourceValuesChange,
  concatenationSourceMap
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([])

  const filteractionDataSource = dataSources?.find(
    (dataSource): dataSource is FilteractionDataSource =>
      dataSource.type === 'filteraction'
  )

  const {
    filteraction: filteractionFromSource,
    interpolated_params: filteractionParamsFromSource,
    endpoint: endpointFromSource
  } = filteractionDataSource?.config || {}

  const filteraction = filteractionFromSource || legacyFilteraction
  const filteractionParams =
    filteractionParamsFromSource || legacyFilteractionParams
  const endpoint = endpointFromSource || legacyEndpoint

  const { values, initialSourceValuesSet, hasAllSourceValues } =
    useSourceActivityValues({
      filteractionParams,
      parentPath,
      onSourceValuesChange
    })

  const ignoreResponse = useRef(false)

  const fetchSuggestionsSyncedArgs = useRef<FetchSuggestionsArgsProps>({
    filteractionParams,
    filteraction,
    endpoint,
    showLoading
  })

  const dispatch = useAppDispatch()

  const resetSuggestions = () => {
    debouncedFetchSuggestions.cancel()
    ignoreResponse.current = true

    setIsLoading(false)
    setSuggestions([])
  }

  useEffect(() => {
    fetchSuggestionsSyncedArgs.current = {
      filteractionParams,
      filteraction,
      endpoint,
      showLoading
    }
  }, [endpoint, showLoading, filteraction, filteractionParams])

  const fetchSuggestions = useCallback(
    async (value: string) => {
      if (!initialSourceValuesSet) return

      ignoreResponse.current = false

      const { filteractionParams, filteraction, endpoint, showLoading } =
        fetchSuggestionsSyncedArgs.current

      try {
        setIsLoading(true)

        const throwError = false
        const sourceActivityValues = hasAllSourceValues ? values : undefined
        const concatenationProjectFields = concatenationSourceMap
          ? getProjectFields(concatenationSourceMap)
          : undefined

        const response = await api.getAutocomplete(
          value,
          endpoint || '',
          !!showLoading,
          filteraction,
          filteractionParams as FilteractionParams[],
          throwError,
          projectFields || concatenationProjectFields,
          sourceActivityValues
        )

        if (response.objects) {
          setSuggestions(
            !Array.isArray(response.objects) ? [] : response.objects
          )
        } else {
          if (!ignoreResponse.current) {
            throw new Error(response?.message || 'Response vazia ou com erro')
          }
        }
      } catch (error) {
        if (!ignoreResponse.current) {
          if (error instanceof Response) {
            dispatch({ type: 'api/ERROR', data: { status: error.status } })
          }

          console.error(error)
        }
      } finally {
        setIsLoading(false)
      }
    },
    [
      dispatch,
      projectFields,
      hasAllSourceValues,
      values,
      initialSourceValuesSet
    ]
  )

  const debouncedFetchSuggestions = useMemo(
    () => debounce(fetchSuggestions, 350),
    [fetchSuggestions]
  )

  return {
    suggestions,
    setSuggestions,
    isLoading,
    fetchSuggestions,
    debouncedFetchSuggestions,
    resetSuggestions,
    hasAllSourceValues,
    initialSourceValuesSet
  }
}

export default useFetchSuggestions
